import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { usePermsReady } from '@/hooks/useEffectivePermissions';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { TimeRangeInput, type TimeRangeInputValue, type TimeRangePreset } from '@/components/TimeRangeInput';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import type { ConditionCatalog, ConditionSetsResponse } from '@/components/conditions/types';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/staffing` (v0.4 章④ F-3、§6-1b / D-5 / D-76 / D-109)。
 * 簡易モード (「この時間帯に◯名」) / 詳細モード (人時・ORグループ・対象者条件) の2段。
 * ⚠️ node_id 単位の GET /staffing/constraints は「そのノード自身」の定義しか返さない。
 * 実効値 (継承先) は GET /staffing/constraints/source で解決し、自ノードに定義が無ければ
 * 継承元ノードの定義を読み取り専用で表示する (D-59、出所ノードを必ず表示)。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-7 / §4-11
 */

const STAFFING_USAGE_POINT_ID = 'staffing_constraint_subject';
const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

type Constraint = {
  constraint_id: string; condition_set_id: string | null;
  measure: 'headcount' | 'labor_hours'; comparator: 'min' | 'max' | 'target'; value: number; sort_order: number;
  // v0.4 追加要件⑬ (D-396): 簡易欄の内数のみ非 null (詳細モードの制約は常に null)
  subset_kind: 'skill' | 'employment_type' | 'position_level' | null;
  subset_target_id: string | null;
};
type Group = {
  group_id: string; node_id: string | null; parent_group_id: string | null;
  satisfy_mode: 'all' | 'any';
  applies_day_of_week: number[] | null; applies_date: string | null;
  applies_start_time: string | null; applies_end_time: string | null;
  label: string | null; is_simple: boolean;
  constraints: Constraint[]; children: Group[];
};
type SourceInfo = { node_id: string; source_node_id: string | null; source_kind: InheritanceSourceKind };

const MEASURE_LABELS: Record<string, string> = { headcount: '人数', labor_hours: '人時' };
const COMPARATOR_LABELS: Record<string, string> = { min: '以上', max: '以下', target: '目標' };

const summarizeApplies = (g: Group): string => {
  const parts: string[] = [];
  if (g.applies_day_of_week && g.applies_day_of_week.length > 0 && g.applies_day_of_week.length < 7) {
    parts.push(g.applies_day_of_week.map((d) => DOW_LABELS[d]).join('・') + '曜日');
  }
  if (g.applies_date) parts.push(g.applies_date);
  // ⚠️ 時間指定なし = 00:00〜24:00 の24時間窓として評価される (evaluate.ts の resolveGroupWindowMinutes)。
  // 「終日」とだけ書くと営業時間中の意味に読めるが、人数要件は「その瞬間に何名いるか」で判定するため
  // 24時間営業でない限り永久に不足になる。実際の意味が見えるよう時刻を併記する (2026-09-03)。
  if (g.applies_start_time || g.applies_end_time) {
    // 片側だけの指定も API は許容する。評価側は欠けた側を 00:00 / 24:00 で補うので、そのまま見せる。
    parts.push(`${g.applies_start_time ?? '00:00'}〜${g.applies_end_time ?? '24:00'}`);
  } else parts.push('00:00〜24:00 (終日)');
  return parts.join(' ・ ');
};

/** ノードの実効営業時間を「時間帯プリセット」に変換する。
 *  §6-0 原則2: 内部表現は開始〜終了時刻のまま持ち、「営業時間に合わせる」は入力の簡略形として提供する。
 *  D-77 が営業時間の用途として明示的に許した「入力支援 (既定値の提示)」にあたる
 *  (シフトパターンの時刻のように営業時間から導出するのではない)。 */
type BusinessHoursResponse = {
  days: { day_of_week: number; value: { open_time: string | null; close_time: string | null; is_closed: boolean } | null }[];
};

const useBusinessHoursPresets = (nodeId: string): TimeRangePreset[] => {
  const { data } = useQuery<BusinessHoursResponse>({
    queryKey: ['business-hours', nodeId],
    queryFn: () => apiFetch<BusinessHoursResponse>(`/settings/business-hours?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  return useMemo(() => {
    const seen = new Map<string, TimeRangePreset>();
    for (const d of data?.days ?? []) {
      const v = d.value;
      if (!v || v.is_closed || !v.open_time || !v.close_time) continue;
      const key = `${v.open_time}-${v.close_time}`;
      if (!seen.has(key)) seen.set(key, { id: `bh-${key}`, label: `営業時間 (${v.open_time}〜${v.close_time})`, startTime: v.open_time, endTime: v.close_time });
    }
    return [...seen.values()];
  }, [data]);
};

type SaveResponse = { group_id: string; warnings?: string[] };

/** 保存 API が返す soft warning (営業時間を超える人数要件など) を表示する。
 *  ⚠️ 主たる導線はモーダル内のインライン注意 (AllDayNotice)。ここは API を直接叩く経路や
 *  時間帯を明示指定して営業時間を外れた場合の保険。 */
const showSaveWarnings = (res: SaveResponse) => {
  for (const w of res.warnings ?? []) toast.warning(w);
};

/** 「終日」を選んでいる間だけ出す注意書き。
 *  ⚠️ サーバ側の判定 (buildBusinessHoursWarnings) を作り直したものではなく、
 *  「終日 = 00:00〜24:00 として判定される」という事実の提示に留めている (§8-134: 同じ判定を
 *  2箇所に実装しない)。営業時間を取得できているときだけ具体値を併記する。 */
function AllDayNotice({ value, presets }: { value: TimeRangeInputValue; presets: TimeRangePreset[] }) {
  if (value.startTime !== null || value.endTime !== null) return null;
  return (
    <p className="text-xs text-alert-text bg-alert-bg border border-alert-border rounded px-2 py-1.5">
      終日は 00:00〜24:00 として判定されます。
      {presets.length > 0 && `この店舗の営業時間は ${presets.map((p) => `${p.startTime}〜${p.endTime}`).join(' / ')} です。`}
      人数の要件は「その瞬間に何名いるか」で見るため、営業時間外を含むと常に不足と表示されます。
    </p>
  );
}

function NodeSelector({ nodeId, onChange }: { nodeId: string; onChange: (id: string) => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  return (
    <div className="min-w-0 w-full sm:w-72">
      <label className="block text-xs text-text-sub mb-1">対象ノード</label>
      <Select value={nodeId || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
        <SelectContent>
          {flatNodes.map(({ node, depth }) => (
            <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 簡易モード
// -----------------------------------------------------------------------------
type SimpleSubsetKind = 'skill' | 'employment_type' | 'position_level';
type SimpleSubsetDraft = { local_id: string; kind: SimpleSubsetKind; target_id: string; headcount: string };
const SUBSET_KIND_LABELS: Record<SimpleSubsetKind, string> = { skill: 'スキル', employment_type: '雇用形態', position_level: '役職・等級' };
// ⚠️ apps/api/src/routes/staffing.ts の同名定数と値を揃えること (API 側が実際の上限を強制する zod
// バリデーション、こちらは「これ以上追加」ボタンを早期に無効化する UI 側の複製)。
const SIMPLE_SUBSET_MAX = 5;
let subsetLocalIdCounter = 0;
const nextSubsetLocalId = () => `subset-${++subsetLocalIdCounter}`;

type SimpleDraft = {
  group_id: string | null;
  applies_day_of_week: number[];
  value: TimeRangeInputValue;
  label: string;
  headcount: string;
  // v0.4 追加要件⑬ (D-396): 既定は空 (§6-0 原則6、「+ 条件を追加」を押したときだけ現れる)
  subsets: SimpleSubsetDraft[];
};
const emptySimpleDraft = (): SimpleDraft => ({ group_id: null, applies_day_of_week: [], value: { startTime: null, endTime: null }, label: '', headcount: '1', subsets: [] });

type SubsetOption = { id: string; label: string };

function SimpleGroupModal({ draft, setDraft, targetNodeId, onClose, onSaved }: {
  draft: SimpleDraft; setDraft: (d: SimpleDraft) => void; targetNodeId: string | null;
  onClose: () => void; onSaved: () => void;
}) {
  const { data: skills = [] } = useQuery<{ skill_id: string; label: string }[]>({ queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills') });
  const { data: employmentTypes = [] } = useQuery<{ type_id: string; label: string }[]>({ queryKey: ['employment-types'], queryFn: () => apiFetch('/employment-types') });
  const { data: positionLevels = [] } = useQuery<{ position_level_id: string; label: string }[]>({ queryKey: ['position-levels'], queryFn: () => apiFetch('/masters/position-levels') });

  const optionsByKind: Record<SimpleSubsetKind, SubsetOption[]> = {
    skill: skills.map((s) => ({ id: s.skill_id, label: s.label })),
    employment_type: employmentTypes.map((t) => ({ id: t.type_id, label: t.label })),
    position_level: positionLevels.map((l) => ({ id: l.position_level_id, label: l.label })),
  };
  // 「スキル」種別は、そのテナントにスキルが0件なら選択肢自体を出さない (§6-0 原則3)
  const availableKinds: SimpleSubsetKind[] = (['skill', 'employment_type', 'position_level'] as const)
    .filter((k) => k !== 'skill' || skills.length > 0);

  const businessHoursPresets = useBusinessHoursPresets(targetNodeId ?? '');
  const saveMut = useMutation({
    mutationFn: () => {
      const subsets = draft.subsets.map((s) => ({ kind: s.kind, target_id: s.target_id, headcount: Number(s.headcount) || 0 }));
      const payload = {
        applies_day_of_week: draft.applies_day_of_week.length > 0 && draft.applies_day_of_week.length < 7 ? draft.applies_day_of_week : null,
        applies_start_time: draft.value.startTime, applies_end_time: draft.value.endTime,
        label: draft.label.trim() || null,
        headcount: Number(draft.headcount) || 0,
        subsets,
      };
      return draft.group_id
        ? apiFetch<SaveResponse>(`/staffing/constraints/${draft.group_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch<SaveResponse>('/staffing/constraints', { method: 'POST', body: JSON.stringify({ mode: 'simple', node_id: targetNodeId, ...payload }) });
    },
    onSuccess: (res: SaveResponse) => { onSaved(); toast.success('保存しました'); showSaveWarnings(res); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const toggleDay = (d: number) => {
    setDraft({ ...draft, applies_day_of_week: draft.applies_day_of_week.includes(d) ? draft.applies_day_of_week.filter((x) => x !== d) : [...draft.applies_day_of_week, d] });
  };

  const addSubset = () => {
    if (draft.subsets.length >= SIMPLE_SUBSET_MAX || availableKinds.length === 0) return;
    const kind = availableKinds[0]!;
    const firstOption = optionsByKind[kind][0];
    setDraft({ ...draft, subsets: [...draft.subsets, { local_id: nextSubsetLocalId(), kind, target_id: firstOption?.id ?? '', headcount: '1' }] });
  };
  const updateSubset = (localId: string, patch: Partial<SimpleSubsetDraft>) => {
    setDraft({ ...draft, subsets: draft.subsets.map((s) => (s.local_id === localId ? { ...s, ...patch } : s)) });
  };
  const removeSubset = (localId: string) => setDraft({ ...draft, subsets: draft.subsets.filter((s) => s.local_id !== localId) });

  const total = Number(draft.headcount) || 0;
  const canSave = total >= 0 && draft.subsets.every((s) => s.target_id && Number(s.headcount) >= 0);

  return (
    <Modal open title={draft.group_id ? '必要人員を編集' : '必要人員を追加'} onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">曜日 (未選択=全曜日)</label>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="曜日">
            {DOW_LABELS.map((label, d) => (
              <button key={d} type="button" aria-pressed={draft.applies_day_of_week.includes(d)} onClick={() => toggleDay(d)}
                className={`min-w-[40px] min-h-[40px] rounded-full text-sm border ${
                  draft.applies_day_of_week.includes(d) ? 'bg-misespark-navy text-white border-misespark-navy' : 'border-surface-border text-text-body hover:bg-surface-sunken'
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <TimeRangeInput
          value={draft.value} onChange={(v) => setDraft({ ...draft, value: v })}
          allowAllDay presets={businessHoursPresets}
          legend="時間帯 (未指定は 00:00〜24:00)"
        />
        <AllDayNotice value={draft.value} presets={businessHoursPresets} />
        <div>
          <label className="block text-sm text-text-sub mb-1">合計人数</label>
          <input type="number" min={0} value={draft.headcount} onChange={(e) => setDraft({ ...draft, headcount: e.target.value })} className={inputCls} />
          {draft.subsets.length > 0 && (
            <p className="text-xs text-text-sub mt-1" aria-live="polite">
              合計{total}名 (うち {draft.subsets.map((s) => `${SUBSET_KIND_LABELS[s.kind]}${Number(s.headcount) || 0}名`).join('・')})
            </p>
          )}
        </div>

        {/* v0.4 追加要件⑬ (D-396): 内数は既定で空、明示的に追加したときだけ現れる (§6-0 原則6) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm text-text-sub">内訳 (任意)</label>
            <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addSubset} disabled={draft.subsets.length >= SIMPLE_SUBSET_MAX || availableKinds.length === 0}>
              条件を追加
            </Button>
          </div>
          {draft.subsets.map((s) => (
            <div key={s.local_id} className="flex flex-wrap items-center gap-2 border border-surface-line rounded p-2">
              <div className="flex-1 min-w-[140px]">
                <Select value={s.kind} onValueChange={(v) => {
                  const kind = v as SimpleSubsetKind;
                  const firstOption = optionsByKind[kind][0];
                  updateSubset(s.local_id, { kind, target_id: firstOption?.id ?? '' });
                }}>
                  <SelectTrigger aria-label="種別" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {availableKinds.map((k) => <SelectItem key={k} value={k}><span className="block truncate">{SUBSET_KIND_LABELS[k]}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[140px]">
                <Select value={s.target_id} onValueChange={(v) => updateSubset(s.local_id, { target_id: v })}>
                  <SelectTrigger aria-label="対象" className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {optionsByKind[s.kind].map((o) => <SelectItem key={o.id} value={o.id}><span className="block truncate">{o.label}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <input type="number" min={0} value={s.headcount} onChange={(e) => updateSubset(s.local_id, { headcount: e.target.value })}
                className="w-20 min-h-[44px] px-2 py-2 rounded border border-surface-border focus-ring" aria-label="人数" />
              <button type="button" aria-label="この条件を削除" onClick={() => removeSubset(s.local_id)}
                className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
            </div>
          ))}
          {draft.subsets.length > 0 && (
            <p className="text-xs text-text-sub">
              内訳は合計{total}名の内数です (追加ではありません)。各行は別々の人でかまいません。
              1人が複数の行の条件を満たす場合、その人はどの行にも数えられます。
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm text-text-sub mb-1">メモ (任意)</label>
          <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} />
        </div>
      </div>
    </Modal>
  );
}

function SimpleModeView({ groups, isOwn, canManage, targetNodeId, onRequestOwn, onChanged }: {
  groups: Group[]; isOwn: boolean; canManage: boolean; targetNodeId: string;
  onRequestOwn: () => void; onChanged: () => void;
}) {
  const [draft, setDraft] = useState<SimpleDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const simpleGroups = groups.filter((g) => g.is_simple);
  // v0.4 追加要件⑬ (D-396): 一覧の動的サマリ用のラベル解決 (モーダルと同じ queryKey でキャッシュ共有)
  const { data: skills = [] } = useQuery<{ skill_id: string; label: string }[]>({ queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills') });
  const { data: employmentTypes = [] } = useQuery<{ type_id: string; label: string }[]>({ queryKey: ['employment-types'], queryFn: () => apiFetch('/employment-types') });
  const { data: positionLevels = [] } = useQuery<{ position_level_id: string; label: string }[]>({ queryKey: ['position-levels'], queryFn: () => apiFetch('/masters/position-levels') });
  const labelForSubset = (c: Constraint): string => {
    if (!c.subset_kind || !c.subset_target_id) return '';
    const list = c.subset_kind === 'skill' ? skills : c.subset_kind === 'employment_type' ? employmentTypes : positionLevels;
    const idField = c.subset_kind === 'skill' ? 'skill_id' : c.subset_kind === 'employment_type' ? 'type_id' : 'position_level_id';
    const found = (list as Array<Record<string, unknown>>).find((item) => item[idField] === c.subset_target_id);
    return (found?.label as string | undefined) ?? c.subset_target_id;
  };

  const deleteMut = useMutation({
    mutationFn: (groupId: string) => apiFetch(`/staffing/constraints/${groupId}`, { method: 'DELETE' }),
    onSuccess: () => { onChanged(); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  return (
    <div className="space-y-3">
      {!isOwn && canManage && (
        <div className="flex items-center justify-between flex-wrap gap-2 rounded border border-alert-border bg-alert-bg p-3">
          <p className="text-xs text-alert-text">上位ノードの設定を継承しています。この店舗専用の設定を追加すると、以降はこの店舗独自の定義になります。</p>
          <Button variant="secondary" size="sm" onClick={onRequestOwn}>この店舗の設定を追加する</Button>
        </div>
      )}
      <div className="flex items-center justify-end">
        {isOwn && canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptySimpleDraft())}>
            必要人員を追加
          </Button>
        )}
      </div>
      {simpleGroups.length === 0 ? (
        <EmptyState message="必要人員がまだ設定されていません" description="未設定の間、この時間帯の必要人員は判定されません。" />
      ) : (
        <ul className="space-y-2">
          {simpleGroups.map((g) => {
            const total = g.constraints.find((c) => c.condition_set_id === null);
            const subsets = g.constraints.filter((c) => c.condition_set_id !== null);
            return (
              <li key={g.group_id} className="flex items-center justify-between gap-2 flex-wrap border border-surface-border rounded-lg bg-white p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-body">{summarizeApplies(g)}</p>
                  <p className="text-xs text-text-sub mt-0.5">
                    必要人数: <span className="font-semibold text-text-body">{total?.value ?? 0}名</span>
                    {subsets.length > 0 && ` (うち ${subsets.map((c) => `${labelForSubset(c)}${c.value}名`).join('・')})`}
                    {g.label && ` ・ ${g.label}`}
                  </p>
                </div>
                {isOwn && canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setDraft({
                      group_id: g.group_id, applies_day_of_week: g.applies_day_of_week ?? [],
                      value: { startTime: g.applies_start_time, endTime: g.applies_end_time },
                      label: g.label ?? '', headcount: String(total?.value ?? 0),
                      subsets: subsets
                        .filter((c): c is Constraint & { subset_kind: SimpleSubsetKind; subset_target_id: string } => !!c.subset_kind && !!c.subset_target_id)
                        .map((c) => ({ local_id: nextSubsetLocalId(), kind: c.subset_kind, target_id: c.subset_target_id, headcount: String(c.value) })),
                    })}>編集</Button>
                    <button type="button" aria-label="削除" onClick={() => setDeleteTarget(g)}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {groups.some((g) => !g.is_simple) && (
        <p className="text-xs text-text-sub">
          詳細モードで編集された設定が {groups.filter((g) => !g.is_simple).length} 件あります (詳細モードで確認・編集してください)。
        </p>
      )}

      {draft && <SimpleGroupModal draft={draft} setDraft={setDraft} targetNodeId={targetNodeId} onClose={() => setDraft(null)} onSaved={() => { onChanged(); setDraft(null); }} />}

      <ConfirmDialog open={!!deleteTarget} title="必要人員を削除" message="この設定を削除します。この操作は取り消せません。"
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.group_id)} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// 詳細モード
// -----------------------------------------------------------------------------
type ConstraintDraft = { condition_set_id: string | null; measure: 'headcount' | 'labor_hours'; comparator: 'min' | 'max' | 'target'; value: string };
type GroupDraft = {
  group_id: string | null; parent_group_id: string | null;
  satisfy_mode: 'all' | 'any';
  applies_day_of_week: number[]; applies_date: string; value: TimeRangeInputValue;
  label: string; constraints: ConstraintDraft[];
};
const emptyGroupDraft = (parentGroupId: string | null): GroupDraft => ({
  group_id: null, parent_group_id: parentGroupId, satisfy_mode: 'all',
  applies_day_of_week: [], applies_date: '', value: { startTime: null, endTime: null },
  label: '', constraints: [{ condition_set_id: null, measure: 'headcount', comparator: 'min', value: '1' }],
});

function DetailedGroupModal({ draft, setDraft, targetNodeId, conditionSets, onClose, onSaved }: {
  draft: GroupDraft; setDraft: (d: GroupDraft) => void; targetNodeId: string | null;
  conditionSets: { set_id: string; label: string | null }[];
  onClose: () => void; onSaved: () => void;
}) {
  const businessHoursPresets = useBusinessHoursPresets(targetNodeId ?? '');
  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        satisfy_mode: draft.satisfy_mode,
        applies_day_of_week: draft.applies_day_of_week.length > 0 && draft.applies_day_of_week.length < 7 ? draft.applies_day_of_week : null,
        applies_date: draft.applies_date || null,
        applies_start_time: draft.value.startTime, applies_end_time: draft.value.endTime,
        label: draft.label.trim() || null,
        constraints: draft.constraints.map((c, i) => ({
          condition_set_id: c.condition_set_id, measure: c.measure, comparator: c.comparator, value: Number(c.value) || 0, sort_order: i,
        })),
      };
      return draft.group_id
        ? apiFetch<SaveResponse>(`/staffing/constraints/${draft.group_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch<SaveResponse>('/staffing/constraints', { method: 'POST', body: JSON.stringify({ mode: 'detailed', node_id: targetNodeId, parent_group_id: draft.parent_group_id, ...payload }) });
    },
    onSuccess: (res: SaveResponse) => { onSaved(); toast.success('保存しました'); showSaveWarnings(res); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const toggleDay = (d: number) => {
    setDraft({ ...draft, applies_day_of_week: draft.applies_day_of_week.includes(d) ? draft.applies_day_of_week.filter((x) => x !== d) : [...draft.applies_day_of_week, d] });
  };
  const updateConstraint = (i: number, patch: Partial<ConstraintDraft>) => {
    setDraft({ ...draft, constraints: draft.constraints.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  };
  const addConstraint = () => setDraft({ ...draft, constraints: [...draft.constraints, { condition_set_id: null, measure: 'headcount', comparator: 'min', value: '1' }] });
  const removeConstraint = (i: number) => setDraft({ ...draft, constraints: draft.constraints.filter((_, idx) => idx !== i) });

  const canSave = draft.constraints.length > 0 && draft.constraints.every((c) => c.value.trim() !== '');

  return (
    <Modal open title={draft.group_id ? 'グループを編集' : (draft.parent_group_id ? '子グループを追加' : 'グループを追加')} onClose={onClose} size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">組み合わせ方</label>
          <div className="flex rounded-full border border-surface-border overflow-hidden w-fit" role="group" aria-label="組み合わせ方">
            {(['all', 'any'] as const).map((mode) => (
              <button key={mode} type="button" aria-pressed={draft.satisfy_mode === mode} onClick={() => setDraft({ ...draft, satisfy_mode: mode })}
                className={`px-3 min-h-[40px] text-xs font-medium ${draft.satisfy_mode === mode ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
                {mode === 'all' ? 'すべて満たす (AND)' : 'いずれか満たす (OR、複数の充足パターン)' }
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">適用曜日 (未選択=全曜日)</label>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="曜日">
            {DOW_LABELS.map((label, d) => (
              <button key={d} type="button" aria-pressed={draft.applies_day_of_week.includes(d)} onClick={() => toggleDay(d)}
                className={`min-w-[40px] min-h-[40px] rounded-full text-sm border ${
                  draft.applies_day_of_week.includes(d) ? 'bg-misespark-navy text-white border-misespark-navy' : 'border-surface-border text-text-body hover:bg-surface-sunken'
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">特定日 (任意、イベント等の一時上振れ)</label>
            <input type="date" value={draft.applies_date} onChange={(e) => setDraft({ ...draft, applies_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">メモ (任意)</label>
            <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} />
          </div>
        </div>
        <TimeRangeInput
          value={draft.value} onChange={(v) => setDraft({ ...draft, value: v })}
          allowAllDay presets={businessHoursPresets}
          legend="時間帯 (未指定は 00:00〜24:00)"
        />
        <AllDayNotice value={draft.value} presets={businessHoursPresets} />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm text-text-sub">制約</label>
            <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addConstraint}>制約を追加</Button>
          </div>
          {draft.constraints.map((c, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 border border-surface-line rounded p-2">
              <div className="flex-1 min-w-[160px]">
                <Select value={c.condition_set_id ?? '__all__'} onValueChange={(v) => updateConstraint(i, { condition_set_id: v === '__all__' ? null : v })}>
                  <SelectTrigger aria-label="対象者条件"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">全員 (条件なし)</SelectItem>
                    {conditionSets.map((s) => <SelectItem key={s.set_id} value={s.set_id}>{s.label ?? s.set_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28 shrink-0">
                <Select value={c.measure} onValueChange={(v) => updateConstraint(i, { measure: v as ConstraintDraft['measure'] })}>
                  <SelectTrigger aria-label="測定量"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MEASURE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="w-24 shrink-0">
                <Select value={c.comparator} onValueChange={(v) => updateConstraint(i, { comparator: v as ConstraintDraft['comparator'] })}>
                  <SelectTrigger aria-label="比較"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(COMPARATOR_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <input type="number" step="0.5" value={c.value} onChange={(e) => updateConstraint(i, { value: e.target.value })}
                className="w-20 min-h-[44px] px-2 py-2 rounded border border-surface-border focus-ring" aria-label="値" />
              <button type="button" aria-label="この制約を削除" onClick={() => removeConstraint(i)} disabled={draft.constraints.length <= 1}
                className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text disabled:opacity-30 focus-ring"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function GroupCard({ group, depth, isOwn, canManage, conditionSets, onEdit, onAddChild, onDelete }: {
  group: Group; depth: number; isOwn: boolean; canManage: boolean; conditionSets: { set_id: string; label: string | null }[];
  onEdit: (g: Group) => void; onAddChild: (g: Group) => void; onDelete: (g: Group) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const setLabel = (id: string | null) => (id ? (conditionSets.find((s) => s.set_id === id)?.label ?? id) : '全員');
  return (
    <div className={depth > 0 ? 'ml-0 sm:ml-5 pl-0 sm:pl-3 border-0 sm:border-l-2 border-surface-border' : ''}>
      <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <button type="button" onClick={() => setExpanded((v) => !v)} className="flex items-center gap-1.5 min-h-[32px] text-left">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
              {group.satisfy_mode === 'all' ? 'AND' : 'OR'}
            </span>
            <span className="text-sm font-semibold text-text-body break-words">{group.label ?? summarizeApplies(group)}</span>
          </button>
          {isOwn && canManage && (
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => onAddChild(group)}>子グループ</Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(group)}>編集</Button>
              <button type="button" aria-label="削除" onClick={() => onDelete(group)}
                className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
            </div>
          )}
        </div>
        {expanded && (
          <>
            <p className="text-xs text-text-sub">{summarizeApplies(group)}</p>
            <ul className="text-xs text-text-body space-y-1">
              {group.constraints.map((c) => (
                <li key={c.constraint_id} className="flex items-center gap-1 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-surface-sunken border border-surface-border">{setLabel(c.condition_set_id)}</span>
                  <span>が {MEASURE_LABELS[c.measure]} {c.value}{c.measure === 'labor_hours' ? '時間' : '名'} {COMPARATOR_LABELS[c.comparator]}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {expanded && group.children.length > 0 && (
        <div className="mt-2 space-y-2">
          {group.children.map((child) => (
            <GroupCard key={child.group_id} group={child} depth={depth + 1} isOwn={isOwn} canManage={canManage} conditionSets={conditionSets}
              onEdit={onEdit} onAddChild={onAddChild} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

function DetailedModeView({ groups, isOwn, canManage, targetNodeId, onRequestOwn, onChanged }: {
  groups: Group[]; isOwn: boolean; canManage: boolean; targetNodeId: string;
  onRequestOwn: () => void; onChanged: () => void;
}) {
  const [draft, setDraft] = useState<GroupDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const [showConditionEditor, setShowConditionEditor] = useState(false);

  const { data: catalog } = useQuery<ConditionCatalog>({ queryKey: ['condition-catalog'], queryFn: () => apiFetch('/condition-catalog'), staleTime: 5 * 60 * 1000 });
  const { data: setsData } = useQuery<ConditionSetsResponse>({
    queryKey: ['condition-sets', STAFFING_USAGE_POINT_ID],
    queryFn: () => apiFetch(`/condition-sets?usage_point_id=${STAFFING_USAGE_POINT_ID}`),
  });
  const usagePoint = catalog?.usage_points.find((u) => u.usage_point_id === STAFFING_USAGE_POINT_ID);
  const attributeTypesById = useMemo(() => new Map((catalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t])), [catalog]);
  const conditionSets = useMemo(() => (setsData?.sets ?? []).map((s) => ({ set_id: s.set_id, label: s.label })), [setsData]);

  const deleteMut = useMutation({
    mutationFn: (groupId: string) => apiFetch(`/staffing/constraints/${groupId}`, { method: 'DELETE' }),
    onSuccess: () => { onChanged(); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const rootGroups = groups.filter((g) => g.parent_group_id === null);

  return (
    <div className="space-y-3">
      {!isOwn && canManage && (
        <div className="flex items-center justify-between flex-wrap gap-2 rounded border border-alert-border bg-alert-bg p-3">
          <p className="text-xs text-alert-text">上位ノードの設定を継承しています。この店舗専用の設定を追加すると、以降はこの店舗独自の定義になります。</p>
          <Button variant="secondary" size="sm" onClick={onRequestOwn}>この店舗の設定を追加する</Button>
        </div>
      )}

      {usagePoint && (
        <div className="border border-surface-border rounded-lg bg-white">
          <button type="button" onClick={() => setShowConditionEditor((v) => !v)} aria-expanded={showConditionEditor}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm font-semibold text-text-body">
            対象者条件を管理
            {showConditionEditor ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {showConditionEditor && (
            <div className="px-3 pb-4">
              <ConditionSetEditor usagePointId={STAFFING_USAGE_POINT_ID} usagePoint={usagePoint} attributeTypesById={attributeTypesById} />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end">
        {isOwn && canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyGroupDraft(null))}>グループを追加</Button>
        )}
      </div>

      {rootGroups.length === 0 ? (
        <EmptyState message="必要人員がまだ設定されていません" description="未設定の間、この時間帯の必要人員は判定されません。" />
      ) : (
        <div className="space-y-2">
          {rootGroups.map((g) => (
            <GroupCard key={g.group_id} group={g} depth={0} isOwn={isOwn} canManage={canManage} conditionSets={conditionSets}
              onEdit={(target) => setDraft({
                group_id: target.group_id, parent_group_id: target.parent_group_id, satisfy_mode: target.satisfy_mode,
                applies_day_of_week: target.applies_day_of_week ?? [], applies_date: target.applies_date ?? '',
                value: { startTime: target.applies_start_time, endTime: target.applies_end_time },
                label: target.label ?? '',
                constraints: target.constraints.map((c) => ({ condition_set_id: c.condition_set_id, measure: c.measure, comparator: c.comparator, value: String(c.value) })),
              })}
              onAddChild={(parent) => setDraft(emptyGroupDraft(parent.group_id))}
              onDelete={(target) => setDeleteTarget(target)}
            />
          ))}
        </div>
      )}

      {draft && (
        <DetailedGroupModal draft={draft} setDraft={setDraft} targetNodeId={targetNodeId} conditionSets={conditionSets}
          onClose={() => setDraft(null)} onSaved={() => { onChanged(); setDraft(null); }} />
      )}

      <ConfirmDialog open={!!deleteTarget} title="グループを削除" message="このグループと配下の子グループを削除します。この操作は取り消せません。"
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.group_id)} />
    </div>
  );
}

export function StaffingSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const permsReady = usePermsReady();
  const canManage = hasPermission(user?.effective_permissions, 'staffing', 'manage');
  const [nodeId, setNodeId] = useState('');
  const [mode, setMode] = useState<'simple' | 'detailed'>('simple');
  const [forceOwn, setForceOwn] = useState(false);
  const qc = useQueryClient();

  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const nodeNameById = useMemo(() => new Map(flattenNodes(nodesData?.roots ?? []).map(({ node }) => [node.node_id, node.name])), [nodesData]);

  const { data: source } = useQuery<SourceInfo>({
    queryKey: ['staffing-source', nodeId], queryFn: () => apiFetch(`/staffing/constraints/source?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const isOwnActual = source?.source_kind === 'self';
  const isOwn = isOwnActual || forceOwn;
  const effectiveNodeId = isOwn ? nodeId : (source?.source_node_id ?? null);

  const { data: groupsData, isLoading } = useQuery<{ node_id: string | null; groups: Group[] }>({
    queryKey: ['staffing-constraints', effectiveNodeId],
    queryFn: () => apiFetch(`/staffing/constraints${effectiveNodeId ? `?node_id=${encodeURIComponent(effectiveNodeId)}` : ''}`),
    enabled: !!nodeId && (isOwnActual || !!source),
  });

  const onChanged = () => {
    // ⚠️ design-and-code MEDIUM指摘: setForceOwn(false) をここで同期実行すると、再フェッチが完了する
    // 前の一瞬 isOwnActual がまだ false のままで isOwn が false に戻り「上位ノードを継承中」バナーが
    // 一瞬再表示されるチラつきが起きていた。forceOwn はリセットせずに残しても実害はない
    // (isOwnActual が再フェッチ後に true になれば isOwn の結果は同じになる、ノード切替時は
    // NodeSelector の onChange 側で明示的にリセットしている)。
    qc.invalidateQueries({ queryKey: ['staffing-source', nodeId] });
    qc.invalidateQueries({ queryKey: ['staffing-constraints'] });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">必要人員設定</h2>
        <p className="text-sm text-text-sub mt-1">
          時間帯ごとに必要な人数・人時、対象者条件を設定します。簡易モードは人数のみ、詳細モードは人時や対象者条件・複数パターン (OR) を扱えます。
        </p>
        {permsReady && !canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (編集には店舗の管理権限が必要です)。</p>}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <NodeSelector nodeId={nodeId} onChange={(id) => { setNodeId(id); setForceOwn(false); }} />
        <div className="flex rounded-full border border-surface-border overflow-hidden" role="group" aria-label="モード">
          {(['simple', 'detailed'] as const).map((m) => (
            <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}
              className={`px-3 min-h-[44px] text-xs font-medium ${mode === m ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}>
              {m === 'simple' ? '簡易モード' : '詳細モード'}
            </button>
          ))}
        </div>
      </div>

      {!nodeId ? (
        <p className="text-sm text-text-sub">対象ノードを選んでください。</p>
      ) : !source ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-3">
          {!forceOwn && (
            <InheritanceSourceBadge
              sourceKind={source.source_kind}
              sourceNodeLabel={source.source_node_id ? (nodeNameById.get(source.source_node_id) ?? source.source_node_id) : null}
            />
          )}
          {isLoading ? (
            <div className="text-text-sub">読み込み中…</div>
          ) : mode === 'simple' ? (
            <SimpleModeView groups={groupsData?.groups ?? []} isOwn={isOwn} canManage={canManage} targetNodeId={nodeId} onRequestOwn={() => setForceOwn(true)} onChanged={onChanged} />
          ) : (
            <DetailedModeView groups={groupsData?.groups ?? []} isOwn={isOwn} canManage={canManage} targetNodeId={nodeId} onRequestOwn={() => setForceOwn(true)} onChanged={onChanged} />
          )}
        </div>
      )}
    </div>
  );
}
